import { useState, useEffect, useRef } from 'react';
import { NavLink } from 'react-router-dom';
import { ChevronRight, ChevronLeft, Clock, Zap, Shield, Trophy, Copy, Check } from 'lucide-react';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { useModal } from '../context/ModalContext';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../config/firebase';
import AnimatedCounter from '../components/AnimatedCounter';
import { useFeaturedTournaments, useTournamentGameCounts } from '../services/tournamentService';
import TCoinIcon from '../components/TCoinIcon';


function getGameClass(game: string) {
    if (game === 'PUBG') return 'pubg';
    if (game === 'Free Fire') return 'freefire';
    if (game === 'MLBB') return 'mlbb';
    return 'wildrift';
}

function getTimeLeft(start: string) {
    const diff = new Date(start).getTime() - Date.now();
    if (diff <= 0) return 'Starting soon';
    const d = Math.floor(diff / 86400000);
    const h = Math.floor((diff % 86400000) / 3600000);
    const m = Math.floor((diff % 3600000) / 60000);
    if (d > 0) return `${d}d ${h}h`;
    return `${h}h ${m}m`;
}

/** Format a number for display: 890000 → "8.9L", 4800 → "4.8k", 50 → "50" */
function formatStatNumber(n: number): string {
    if (n >= 100000) {
        const lakhs = n / 100000;
        return lakhs % 1 === 0 ? `${lakhs}L` : `${lakhs.toFixed(1)}L`;
    }
    if (n >= 1000) {
        const thousands = n / 1000;
        return thousands % 1 === 0 ? `${thousands}k` : `${thousands.toFixed(1)}k`;
    }
    return `${n}`;
}

export default function Landing() {
    const { user, loginWithGoogle } = useAuth();
    const { showAlert } = useModal();
    const [bgIndex, setBgIndex] = useState(0);

    const handleGoogleSignIn = async () => {
        try {
            await loginWithGoogle();
        } catch (err: any) {
            console.error('Google sign-in error:', err.code, err.message, err);
            if (err.code !== 'auth/popup-closed-by-user') {
                showAlert('Google sign-in failed. Please try again.', 'Error');
            }
        }
    };

    const { tournaments: featured, loading: featuredLoading } = useFeaturedTournaments();
    const { counts: gameCounts } = useTournamentGameCounts();

    const gameData = [
        { name: 'PUBG Mobile', filterName: 'PUBG Mobile', logoUrl: '/games/pubg.png', count: gameCounts?.pubg ?? 0, color: '#f59e0b', bgUrl: '/games/pubg wallpaper.jpg' },
        { name: 'Free Fire', filterName: 'Free Fire', logoUrl: '/games/freefire.jfif', count: gameCounts?.freeFire ?? 0, color: '#ef4444', bgUrl: '/games/ff wallpaper 01.jpg' },
        { name: 'Mobile Legends', filterName: 'MLBB', logoUrl: '/games/mlbb.jpg', count: gameCounts?.mlbb ?? 0, color: '#a855f7', bgUrl: '/games/mlbb wallpaper.jpg' },
        { name: 'Wild Rift', filterName: 'Wild Rift', logoUrl: '/games/wildrift.jfif', count: gameCounts?.wildRift ?? 0, color: '#3b82f6', bgUrl: '/games/wild rift wallpaper 05.jpg' },
    ];

    const [copiedField, setCopiedField] = useState<string | null>(null);
    const handleCopy = (e: React.MouseEvent, text: string) => {
        e.preventDefault();
        e.stopPropagation();
        navigator.clipboard.writeText(text);
        setCopiedField(text);
        setTimeout(() => setCopiedField(null), 2000);
    };

    // ── Game Carousel State ──
    const [carouselIndex, setCarouselIndex] = useState(0);
    const [visibleCards, setVisibleCards] = useState(3);
    const [containerWidth, setContainerWidth] = useState(0);
    const carouselRef = useRef<HTMLDivElement>(null);

    // Responsive: recalculate visible cards and width on resize
    useEffect(() => {
        const updateCarousel = () => {
            const w = window.innerWidth;
            if (w < 600) setVisibleCards(1);
            else if (w < 900) setVisibleCards(2);
            else setVisibleCards(3);

            if (carouselRef.current) {
                setContainerWidth(carouselRef.current.offsetWidth);
            }
        };
        updateCarousel();
        window.addEventListener('resize', updateCarousel);
        return () => window.removeEventListener('resize', updateCarousel);
    }, []);

    const maxCarouselIndex = Math.max(0, gameData.length - visibleCards);
    const canGoLeft = carouselIndex > 0;
    const canGoRight = carouselIndex < maxCarouselIndex;

    const gap = 24;
    const cardWidth = containerWidth > 0 ? (containerWidth - (visibleCards - 1) * gap) / visibleCards : 0;
    const slideDistance = cardWidth + gap;

    // Real-time hero card stats — defaults to 0 until Firestore loads
    const [stats, setStats] = useState({
        tournaments: 0,
        players: 0,
        distribution: 0,
    });

    const heroBgUrls = [
        '/games/pubg wallpaper.jpg',
        '/games/ff wallpaper 01.jpg',
        '/games/mlbb wallpaper.jpg',
        '/games/wild rift wallpaper 05.jpg'
    ];

    useEffect(() => {
        const interval = setInterval(() => {
            setBgIndex(prev => (prev + 1) % heroBgUrls.length);
        }, 5000);
        return () => clearInterval(interval);
    }, []);

    // Real-time Firestore listeners for platform stats
    useEffect(() => {
        const unsubCounts = onSnapshot(
            doc(db, 'platformStats', 'counts'),
            (snap) => {
                if (snap.exists()) {
                    const data = snap.data();
                    setStats(prev => ({
                        ...prev,
                        tournaments: data.totalTournaments ?? prev.tournaments,
                        players: data.totalUsers ?? data.totalPlayers ?? prev.players,
                    }));
                }
            },
            (error) => console.warn('Hero stats (counts) listener error:', error)
        );

        const unsubDist = onSnapshot(
            doc(db, 'platformStats', 'distribution'),
            (snap) => {
                if (snap.exists()) {
                    const data = snap.data();
                    setStats(prev => ({
                        ...prev,
                        distribution: data.totalFlowAmount ?? prev.distribution,
                    }));
                }
            },
            (error) => console.warn('Hero stats (distribution) listener error:', error)
        );

        return () => { unsubCounts(); unsubDist(); };
    }, []);

    return (
        <div className="page-wrapper">
            {/* Hero */}
            <div 
                className="hero animate-in"
                style={{
                    position: 'relative',
                    overflow: 'hidden',
                    backgroundColor: 'rgba(20, 20, 30, 1)'
                }}
            >
                {/* Background Slideshow */}
                {heroBgUrls.map((url, i) => (
                    <div
                        key={url}
                        style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            background: `linear-gradient(to right, rgba(20, 20, 30, 0.95), rgba(20, 20, 30, 0.4)), url("${url}") center/cover no-repeat`,
                            opacity: i === bgIndex ? 1 : 0,
                            transition: 'opacity 1s ease-in-out',
                            zIndex: 0
                        }}
                    />
                ))}
                
                <div className="hero-content" style={{ position: 'relative', zIndex: 1 }}>
                    <h1>
                        Compete. Win.<br />
                        <span className="gradient-text">Get Rewarded.</span>
                    </h1>
                    <p>
                        Bangladesh's premier esports tournament platform. Join skill-based PUBG, Free Fire, Wild Rift & Mobile Legends
                        tournaments — compete with real players, win real credits.
                    </p>
                    <div className="hero-buttons" style={{ display: 'flex', gap: 12, marginBottom: 32 }}>
                        <NavLink to="/tournaments"><button className="btn btn-primary btn-lg">Browse Tournaments</button></NavLink>
                        {!user && <button className="btn btn-secondary btn-lg" onClick={handleGoogleSignIn}>🔑 Join with Google</button>}
                    </div>
                    <div className="hero-stats">
                        <div className="hero-stat"><div className="stat-val"><AnimatedCounter value={stats.tournaments} formatter={formatStatNumber} /></div><div className="stat-label">Tournaments</div></div>
                        <div className="hero-stat"><div className="stat-val"><AnimatedCounter value={stats.players} formatter={formatStatNumber} /></div><div className="stat-label">Players</div></div>
                        <div className="hero-stat"><div className="stat-val" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}><TCoinIcon size={28} /><AnimatedCounter value={stats.distribution} formatter={formatStatNumber} /></div><div className="stat-label">Distributed</div></div>
                    </div>
                </div>
            </div>

            {/* Game Cards Carousel */}
            <div className="section-header animate-in">
                <h2>🎮 Choose Your Game</h2>
                <div style={{ display: 'flex', gap: 6 }}>
                    <button
                        onClick={() => setCarouselIndex(i => Math.max(0, i - 1))}
                        disabled={!canGoLeft}
                        style={{
                            width: 32, height: 32, borderRadius: 8, border: '1px solid var(--border)',
                            background: canGoLeft ? 'var(--bg-glass)' : 'transparent',
                            color: 'var(--text-secondary)', cursor: canGoLeft ? 'pointer' : 'default',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            opacity: canGoLeft ? 1 : 0.35, transition: 'all 0.2s',
                        }}
                    >
                        <ChevronLeft size={16} />
                    </button>
                    <button
                        onClick={() => setCarouselIndex(i => Math.min(maxCarouselIndex, i + 1))}
                        disabled={!canGoRight}
                        style={{
                            width: 32, height: 32, borderRadius: 8, border: '1px solid var(--border)',
                            background: canGoRight ? 'var(--bg-glass)' : 'transparent',
                            color: 'var(--text-secondary)', cursor: canGoRight ? 'pointer' : 'default',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            opacity: canGoRight ? 1 : 0.35, transition: 'all 0.2s',
                        }}
                    >
                        <ChevronRight size={16} />
                    </button>
                </div>
            </div>
            <div ref={carouselRef} style={{ overflow: 'hidden', marginBottom: 40 }}>
                <motion.div
                    style={{ display: 'flex', gap: gap }}
                    animate={{ x: containerWidth > 0 ? -carouselIndex * slideDistance : 0 }}
                    transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                >
                    {gameData.map((g, i) => (
                        <NavLink
                            to={`/tournaments?game=${g.filterName}`}
                            key={g.name}
                            style={{
                                textDecoration: 'none',
                                minWidth: containerWidth > 0 ? cardWidth : `calc((100% - ${(visibleCards - 1) * gap}px) / ${visibleCards})`,
                                flexShrink: 0,
                            }}
                        >
                            <div
                                className={`game-card animate-in stagger-${i + 1}`}
                                style={{
                                    position: 'relative',
                                    aspectRatio: '16/10',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'flex-start',
                                    alignItems: 'flex-start',
                                    padding: '24px',
                                    borderRadius: '16px',
                                    overflow: 'hidden',
                                    background: g.bgUrl
                                        ? `linear-gradient(to right, rgba(20, 20, 30, 0.95), rgba(20, 20, 30, 0.5)), url("${g.bgUrl}") center/cover no-repeat`
                                        : `linear-gradient(to right, rgba(20, 20, 30, 0.95), rgba(20, 20, 30, 0.4)), ${g.color}15`,
                                    border: '1px solid rgba(255, 255, 255, 0.05)',
                                    transition: 'transform 0.2s, border-color 0.2s',
                                }}
                            >
                                <div
                                    className="game-logo"
                                    style={{
                                        height: '64px',
                                        width: '64px',
                                        borderRadius: '14px',
                                        overflow: 'hidden',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        marginBottom: 'auto',
                                        boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
                                        flexShrink: 0,
                                    }}
                                >
                                    <img src={g.logoUrl} alt={g.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                </div>

                                <div style={{ width: '100%', textAlign: 'left', marginTop: '20px' }}>
                                    <div className="game-name" style={{ fontSize: '1.25rem', fontWeight: 700, marginBottom: '4px', color: '#fff' }}>
                                        {g.name}
                                    </div>

                                    <div className="game-count" style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.85rem', color: 'rgba(255,255,255,0.7)', marginBottom: '16px' }}>
                                        <span style={{ height: '8px', width: '8px', borderRadius: '50%', backgroundColor: g.color }} />
                                        {g.count} active tournaments
                                    </div>

                                    <button
                                        style={{
                                            background: 'transparent',
                                            border: `1px solid ${g.color}50`,
                                            color: g.color,
                                            padding: '6px 16px',
                                            borderRadius: '20px',
                                            fontSize: '0.8rem',
                                            fontWeight: 600,
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '4px',
                                        }}
                                    >
                                        Play Now &rarr;
                                    </button>
                                </div>
                            </div>
                        </NavLink>
                    ))}
                </motion.div>
            </div>

            {/* Featured Tournaments */}
            <div className="section-header animate-in">
                <h2>🏆 Featured Tournaments</h2>
                <NavLink to="/tournaments" className="view-all">See All</NavLink>
            </div>
            <div className="tournament-grid">
                {featuredLoading ? Array.from({ length: 3 }).map((_, i) => (
                    <div key={`skeleton-${i}`} className="tournament-card animate-in shimmer" style={{ height: 280, border: '1px solid rgba(255,255,255,0.05)', background: 'linear-gradient(90deg, rgba(255,255,255,0.02) 25%, rgba(255,255,255,0.06) 50%, rgba(255,255,255,0.02) 75%)', backgroundSize: '400% 100%', animation: 'shimmer 1.5s infinite linear' }} />
                )) : featured.length === 0 ? (
                    <div className="empty-state" style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '60px 20px', background: 'var(--bg-glass)', borderRadius: '16px', border: '1px dashed rgba(255,255,255,0.1)' }}>
                        <div style={{ fontSize: '2.5rem', marginBottom: '16px', opacity: 0.5 }}>🏆</div>
                        <h3 style={{ color: 'var(--text-primary)', marginBottom: '8px', fontSize: '1.2rem' }}>No Active Tournaments</h3>
                        <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', maxWidth: '400px', margin: '0 auto' }}>There are currently no upcoming or live tournaments. Check back later or browse all past tournaments!</p>
                    </div>
                ) : featured.map((t, i) => {
                    const fillPct = Math.round((t.slotFill / t.maxSlots) * 100);
                    const currentPool = Math.round(t.slotFill * t.entryFee * 0.8);
                    const estPool = Math.round(t.maxSlots * t.entryFee * 0.8);
                    return (
                        <NavLink to={`/tournaments/${t.id}`} key={t.id}>
                            <div className={`tournament-card animate-in stagger-${i + 1}`}>
                                <div className="tc-header">
                                    <span className={`tc-game-badge ${getGameClass(t.game)}`}>{t.game} • {t.mode}</span>
                                    <span className={`tc-status ${t.status}`}>
                                        {t.status === 'live' && <span className="pulse-dot" />}
                                        {t.status}
                                    </span>
                                </div>
                                <div className="tc-title">{t.title}</div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8, marginTop: -4 }} onClick={e => e.preventDefault()}>
                                    <span style={{ fontSize: '0.68rem', fontFamily: 'monospace', background: 'rgba(255,255,255,0.05)', padding: '2px 6px', borderRadius: 4, color: 'var(--text-secondary)', border: '1px solid var(--border)' }}>
                                        {t.id}
                                    </span>
                                    <button onClick={(e) => handleCopy(e, t.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: copiedField === t.id ? 'var(--success)' : 'var(--text-muted)', display: 'flex', alignItems: 'center', padding: 2, transition: 'color 0.2s' }} title="Copy ID">
                                        {copiedField === t.id ? <Check size={12} /> : <Copy size={12} />}
                                    </button>
                                </div>
                                <div className="tc-mode">Entry Fee: <TCoinIcon size={12} /> {t.entryFee} • Starts {new Date(t.startTime).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}</div>
                                <div className="tc-info-row">
                                    <div className="tc-info-item"><div className="tc-info-label">Current Pool</div><div className="tc-info-value prize"><TCoinIcon size={14} /> {currentPool.toLocaleString()}</div></div>
                                    <div className="tc-info-item"><div className="tc-info-label">Est. Pool</div><div className="tc-info-value" style={{ color: 'var(--text-muted)' }}><TCoinIcon size={14} /> {estPool.toLocaleString()}</div></div>
                                    <div className="tc-info-item"><div className="tc-info-label">Entry Fee</div><div className="tc-info-value fee"><TCoinIcon size={14} /> {t.entryFee}</div></div>
                                </div>
                                <div className="tc-slots">
                                    <div className="tc-slots-bar">
                                        <motion.div className="tc-slots-fill" 
                                            initial={{ width: 0 }}
                                            animate={{ width: `${fillPct}%` }}
                                            transition={{ duration: 1, ease: "easeOut" }}
                                            style={{ height: '100%', borderRadius: 5, background: fillPct >= 75 ? 'var(--success)' : fillPct >= 50 ? 'var(--warning)' : 'var(--accent)' }} 
                                        />
                                    </div>
                                    <div className="tc-slots-text">
                                        <span>{t.slotFill}/{t.maxSlots} players</span>
                                        <span>{fillPct}% full</span>
                                    </div>
                                </div>
                                <div className="tc-footer">
                                    <div className="tc-countdown"><Clock size={14} />{getTimeLeft(t.startTime)}</div>
                                    <button className="btn btn-primary btn-sm">Join Now</button>
                                </div>
                            </div>
                        </NavLink>
                    );
                })}
            </div>

            {/* How It Works */}
            <div className="section-header animate-in"><h2>⚡ How It Works</h2></div>
            <div className="hiw-grid">
                {[
                    { icon: '📝', title: 'Sign Up', desc: 'Create your account and add your in-game names' },
                    { icon: '💰', title: 'Deposit TCoins', desc: 'Add TCoins via bKash, Nagad, or Rocket (1 T Coin = 1 Bangladeshi Taka)' },
                    { icon: '🎮', title: 'Join & Play', desc: 'Browse tournaments, join with TCoins, and compete' },
                    { icon: '🏆', title: 'Win & Withdraw', desc: 'Win prizes based on placement & kills, withdraw anytime' },
                ].map((step, i) => (
                    <div key={i} className={`hiw-card animate-in stagger-${i + 1}`}>
                        <div className="hiw-num">{i + 1}</div>
                        <h3>{step.icon} {step.title}</h3>
                        <p>{step.desc}</p>
                    </div>
                ))}
            </div>

            {/* Trust Badges */}
            <div className="card animate-in" style={{ display: 'flex', justifyContent: 'center', gap: 48, padding: '28px 40px', marginBottom: 40 }}>
                {[
                    { icon: <Shield size={24} />, label: 'Anti-Cheat Protected' },
                    { icon: <Zap size={24} />, label: 'Instant Results' },
                    { icon: <Trophy size={24} />, label: 'Verified Rewards' },
                ].map((b, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, color: 'var(--text-secondary)', fontSize: '0.88rem', fontWeight: 500 }}>
                        <span style={{ color: 'var(--accent-hover)' }}>{b.icon}</span>{b.label}
                    </div>
                ))}
            </div>
        </div>
    );
}
